import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import WifiOutlinedIcon from '@mui/icons-material/WifiOutlined'
import WifiOffOutlinedIcon from '@mui/icons-material/WifiOffOutlined'
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined'
import {
    getSceneGuestChatMessages,
    markSceneGuestChatRead,
    sendSceneGuestChatMessage,
    updateSceneGuestVisitorName,
} from '../../actions/sceneGuestAccess.actions'
import { getSocket } from '../../socket/socket'
import SecureMessageAttachment from '../../components/media/SecureMessageAttachment'
import SiteMediaViewer from '../../components/media/SiteMediaViewer'
import {
    downloadBlob,
    fetchChatAttachment,
    getChatAttachmentName,
    getChatAttachmentMimeType,
    getChatAttachmentType,
    hasSecureChatAttachment,
    shareBlobFile,
} from '../../utils/chatAttachmentStore'

const getId = (value) => String(value?._id || value?.id || value || '')

const formatTime = (value) => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''

    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const getMessageAuthor = (message, visitor) => {
    if (message?.guestSender) {
        const own = getId(message.guestSender.visitorId) === getId(visitor)
        return own ? 'Вы' : (message.guestSender.name || 'Заказчик')
    }

    const sender = message?.sender || {}
    return [sender.firstName, sender.lastName].filter(Boolean).join(' ').trim() ||
        sender.email ||
        'Менеджер'
}

const mergeMessages = (current, incoming) => {
    const byId = new Map()

    ;[...(current || []), ...(incoming || [])].forEach((message) => {
        const id = getId(message)
        if (id) byId.set(id, message)
    })

    return Array.from(byId.values()).sort((a, b) => (
        new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
    ))
}

const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024

const SceneGuestChatPanel = ({
    guestToken,
    chat,
    visitor,
    pageSessionId,
    onVisitorChange,
}) => {
    const dispatch = useDispatch()
    const [messages, setMessages] = useState([])
    const [text, setText] = useState('')
    const [selectedFile, setSelectedFile] = useState(null)
    const [previewMessageId, setPreviewMessageId] = useState('')
    const [loading, setLoading] = useState(false)
    const [sending, setSending] = useState(false)
    const [error, setError] = useState('')
    const [connected, setConnected] = useState(false)
    const [managerTyping, setManagerTyping] = useState(false)
    const [editingName, setEditingName] = useState(false)
    const [name, setName] = useState(visitor?.name || 'Гость')
    const [savingName, setSavingName] = useState(false)
    const listRef = useRef(null)
    const fileInputRef = useRef(null)
    const typingTimerRef = useRef(null)
    const managerTypingTimerRef = useRef(null)

    const chatId = getId(chat)
    const visitorId = getId(visitor)

    const loadMessages = useCallback(async (silent = false) => {
        if (!guestToken || !chatId || !visitorId) return

        if (!silent) setLoading(true)
        const result = await dispatch(getSceneGuestChatMessages(guestToken, { limit: 100 }))
        if (!silent) setLoading(false)

        if (!result?.ok) {
            if (!silent) setError(result?.error || 'Не удалось загрузить чат')
            return
        }

        setMessages((current) => mergeMessages(current, result.data?.messages || []))
        dispatch(markSceneGuestChatRead(guestToken))
    }, [chatId, dispatch, guestToken, visitorId])

    useEffect(() => {
        setName(visitor?.name || 'Гость')
    }, [visitor?.name])

    useEffect(() => {
        loadMessages(false)
        const timer = window.setInterval(() => loadMessages(true), 20000)
        return () => window.clearInterval(timer)
    }, [loadMessages])

    useEffect(() => {
        if (!guestToken || !chatId || !visitorId) return undefined

        const socket = getSocket()

        const onNewMessage = (message) => {
            if (getId(message?.chatId) !== chatId) return
            setMessages((current) => mergeMessages(current, [message]))
            dispatch(markSceneGuestChatRead(guestToken))
        }
        const onConnect = () => setConnected(true)
        const onDisconnect = () => setConnected(false)
        const onJoined = (response = {}) => {
            setConnected(!!response.ok)
            if (!response.ok) setError(response.error || 'Сокет чата не подключён')
        }
        const onTypingStart = (payload = {}) => {
            if (getId(payload.chatId) !== chatId) return
            setManagerTyping(true)
            window.clearTimeout(managerTypingTimerRef.current)
            managerTypingTimerRef.current = window.setTimeout(() => setManagerTyping(false), 2500)
        }
        const onTypingStop = (payload = {}) => {
            if (getId(payload.chatId) !== chatId) return
            setManagerTyping(false)
        }

        socket.on('connect', onConnect)
        socket.on('disconnect', onDisconnect)
        socket.on('newMessage', onNewMessage)
        socket.on('typing:start', onTypingStart)
        socket.on('typing:stop', onTypingStop)
        socket.on('sceneGuest:joined', onJoined)
        setConnected(socket.connected)

        return () => {
            window.clearTimeout(typingTimerRef.current)
            window.clearTimeout(managerTypingTimerRef.current)
            socket.emit('sceneGuest:typing:stop', { chatId })
            socket.off('connect', onConnect)
            socket.off('disconnect', onDisconnect)
            socket.off('newMessage', onNewMessage)
            socket.off('typing:start', onTypingStart)
            socket.off('typing:stop', onTypingStop)
            socket.off('sceneGuest:joined', onJoined)
        }
    }, [chatId, dispatch, guestToken, pageSessionId, visitorId])

    useEffect(() => {
        const node = listRef.current
        if (!node) return
        node.scrollTop = node.scrollHeight
    }, [messages.length, managerTyping])

    const ownMessageIds = useMemo(() => new Set(
        messages
            .filter((message) => getId(message?.guestSender?.visitorId) === visitorId)
            .map(getId)
    ), [messages, visitorId])

    const imageMessages = useMemo(() => (
        messages.filter((message) => (
            hasSecureChatAttachment(message) &&
            getChatAttachmentType(message) === 'image'
        ))
    ), [messages])

    const previewIndex = useMemo(() => {
        const index = imageMessages.findIndex((message) => getId(message) === previewMessageId)
        return index >= 0 ? index : 0
    }, [imageMessages, previewMessageId])

    const send = async (event) => {
        event?.preventDefault?.()
        const clean = text.trim()
        const file = selectedFile
        if ((!clean && !file) || sending || !guestToken) return

        setSending(true)
        setError('')
        setText('')
        setSelectedFile(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
        getSocket().emit('sceneGuest:typing:stop', { chatId })

        const result = await dispatch(sendSceneGuestChatMessage(guestToken, {
            text: clean,
            file,
        }))
        setSending(false)

        if (!result?.ok) {
            setText(clean)
            setSelectedFile(file)
            setError(result?.error || 'Не удалось отправить сообщение')
            return
        }

        setMessages((current) => mergeMessages(current, [result.data?.message]))
    }

    const handleTyping = (value) => {
        setText(value)
        const socket = getSocket()
        socket.emit('sceneGuest:typing:start', { chatId })
        window.clearTimeout(typingTimerRef.current)
        typingTimerRef.current = window.setTimeout(() => {
            socket.emit('sceneGuest:typing:stop', { chatId })
        }, 1200)
    }

    const handleFileChange = (event) => {
        const file = event.target.files?.[0] || null

        if (!file) {
            setSelectedFile(null)
            return
        }

        if (file.size > MAX_ATTACHMENT_BYTES) {
            setSelectedFile(null)
            event.target.value = ''
            setError('Максимальный размер вложения — 50 МБ')
            return
        }

        setError('')
        setSelectedFile(file)
    }

    const saveName = async () => {
        const clean = name.trim().slice(0, 80)
        if (!clean || savingName) return

        setSavingName(true)
        const result = await dispatch(updateSceneGuestVisitorName(guestToken, clean))
        setSavingName(false)

        if (!result?.ok) {
            setError(result?.error || 'Не удалось изменить имя')
            return
        }

        setEditingName(false)
        onVisitorChange?.(result.data?.visitor || { ...visitor, name: clean })
        getSocket().emit('sceneGuest:join', {
            guestToken,
            pageSessionId,
        })
    }

    const handleDownload = useCallback(async (message) => {
        const asset = await fetchChatAttachment({
            message,
            guestToken,
            download: true,
        })
        downloadBlob({
            blob: asset.blob,
            filename: asset.filename || getChatAttachmentName(message),
        })
    }, [guestToken])

    const handleShare = useCallback(async (message) => {
        const asset = await fetchChatAttachment({ message, guestToken })
        await shareBlobFile({
            blob: asset.blob,
            filename: asset.filename || getChatAttachmentName(message),
            mimeType: asset.mimeType || getChatAttachmentMimeType(message),
            title: 'Фото из чата 3D-сцены',
        })
    }, [guestToken])

    return (
        <>
            <div className="sceneGuestChatPanel">
                <div className="sceneGuestChatPanel__identity">
                    <div>
                        <span>Вы в чате как</span>
                        {editingName ? (
                            <input
                                value={name}
                                maxLength={80}
                                autoFocus
                                onChange={(event) => setName(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') saveName()
                                    if (event.key === 'Escape') setEditingName(false)
                                }}
                            />
                        ) : <strong>{visitor?.name || 'Гость'}</strong>}
                    </div>
                    <div className="sceneGuestChatPanel__identityActions">
                        {editingName ? (
                            <>
                                <button type="button" onClick={saveName} disabled={savingName || !name.trim()}><DoneOutlinedIcon /></button>
                                <button type="button" onClick={() => { setEditingName(false); setName(visitor?.name || 'Гость') }}><CloseOutlinedIcon /></button>
                            </>
                        ) : (
                            <button type="button" onClick={() => setEditingName(true)}><EditOutlinedIcon /></button>
                        )}
                    </div>
                </div>

                <div className="sceneGuestChatPanel__status">
                    {connected ? <WifiOutlinedIcon /> : <WifiOffOutlinedIcon />}
                    <span>{connected ? 'Связь с менеджером активна' : 'Переподключаемся…'}</span>
                </div>

                <div ref={listRef} className="sceneGuestChatPanel__messages">
                    {loading && !messages.length ? <div className="sceneGuestChatPanel__empty">Загружаем сообщения…</div> : null}
                    {!loading && !messages.length ? <div className="sceneGuestChatPanel__empty">Напишите менеджеру по этой 3D-сцене.</div> : null}

                    {messages.map((message) => {
                        const isOwn = ownMessageIds.has(getId(message))
                        const hasAttachment = hasSecureChatAttachment(message)

                        return (
                            <article key={getId(message)} className={`sceneGuestChatPanel__message ${isOwn ? 'is-own' : 'is-office'}`}>
                                <div className="sceneGuestChatPanel__messageHead">
                                    <strong>{getMessageAuthor(message, visitor)}</strong>
                                    <time>{formatTime(message.createdAt)}</time>
                                </div>
                                {message.text ? <p>{message.text}</p> : null}
                                {hasAttachment ? (
                                    <SecureMessageAttachment
                                        message={message}
                                        guestToken={guestToken}
                                        onOpen={() => setPreviewMessageId(getId(message))}
                                    />
                                ) : null}
                            </article>
                        )
                    })}

                    {managerTyping ? <div className="sceneGuestChatPanel__typing">Менеджер печатает…</div> : null}
                </div>

                {error ? <div className="sceneGuestChatPanel__error">{error}</div> : null}

                <form className="sceneGuestChatPanel__composer" onSubmit={send}>
                    <textarea
                        rows="3"
                        value={text}
                        maxLength={4000}
                        placeholder={selectedFile ? `Файл: ${selectedFile.name}` : 'Сообщение менеджеру…'}
                        onChange={(event) => handleTyping(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault()
                                send(event)
                            }
                        }}
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        onChange={handleFileChange}
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={sending}
                        title={selectedFile ? `Выбран файл: ${selectedFile.name}` : 'Прикрепить файл'}
                    >
                        <AttachFileOutlinedIcon />
                        {selectedFile ? 'Заменить файл' : 'Файл'}
                    </button>
                    {selectedFile ? (
                        <button
                            type="button"
                            onClick={() => {
                                setSelectedFile(null)
                                if (fileInputRef.current) fileInputRef.current.value = ''
                            }}
                            disabled={sending}
                            title="Убрать вложение"
                        >
                            <CloseOutlinedIcon />
                            Убрать
                        </button>
                    ) : null}
                    <button type="submit" disabled={sending || (!text.trim() && !selectedFile)}>
                        <SendOutlinedIcon />
                        {sending ? 'Отправляем…' : 'Отправить'}
                    </button>
                </form>
            </div>

            <SiteMediaViewer
                open={!!previewMessageId && imageMessages.length > 0}
                items={imageMessages}
                activeIndex={previewIndex}
                onActiveIndexChange={(nextIndex, nextMessage) => setPreviewMessageId(getId(nextMessage))}
                onClose={() => setPreviewMessageId('')}
                resolveItem={(message) => fetchChatAttachment({ message, guestToken })}
                getItemMimeType={(message) => getChatAttachmentMimeType(message) || 'image/jpeg'}
                getTitle={(message) => getChatAttachmentName(message, 'Фото из чата')}
                getSubtitle={(message) => `${getMessageAuthor(message, visitor)} · ${formatTime(message?.createdAt)}`}
                getAlt={(message) => getChatAttachmentName(message, 'Фото из чата')}
                onDownload={handleDownload}
                onShare={handleShare}
                shareLabel="Поделиться"
                loadingText="Загружаем защищённое фото…"
                emptyText="Фото недоступно"
            />
        </>
    )
}

export default SceneGuestChatPanel
